import React from 'react';

const RetailInvoice = ({ invoice, seller }) => {
  const taxableValue = (invoice.total / 1.18).toFixed(2);
  const gstAmount = (invoice.total - taxableValue).toFixed(2);

  return (
    <div className="p-10 w-[210mm] min-h-[297mm] mx-auto bg-white text-slate-900 font-sans shadow-lg border border-gray-100">
      
      {/* 1. Header with Logo and Store Info */}
      <div className="flex justify-between items-start border-b-2 border-slate-900 pb-4">
        <div className="w-2/3">
          <img src={seller.logo} alt="Brand Logo" className="h-14 mb-3" />
          <h1 className="text-xl font-extrabold uppercase tracking-tight">{seller.name}</h1>
          <p className="text-[11px] leading-relaxed text-slate-600 w-3/4">{seller.address}</p>
          <div className="mt-2 text-[10px] space-y-0.5">
            <p><strong>GSTIN:</strong> {seller.gstin}</p>
            <p><strong>Contact:</strong> {seller.phone} | {seller.email}</p>
          </div>
        </div>
        <div className="w-1/3 text-right">
          <h2 className="text-2xl font-black text-slate-300 uppercase">Retail Invoice</h2>
          <div className="mt-4 text-xs space-y-1">
            <p><strong>Invoice No:</strong> {invoice.invoiceNo}</p>
            <p><strong>Date:</strong> {invoice.invoiceDate}</p>
            <p><strong>Place of Supply:</strong> {invoice.placeOfSupply}</p>
          </div>
        </div>
      </div>

      {/* 2. Customer Section */}
      <div className="my-6 p-3 bg-slate-50 border border-slate-200 rounded flex justify-between">
        <div>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Billed To:</span>
          <p className="font-bold text-sm">{invoice.customerName}</p>
          <p className="text-[11px] text-slate-600">{invoice.shippingAddress}</p>
        </div>
        <div className="text-right">
          <span className="text-[10px] font-bold text-slate-400 uppercase block">Customer GSTIN:</span>
          <p className="text-xs font-mono">{invoice.customerGstin || "N/A"}</p>
        </div>
      </div>

      {/* 3. Items Table - Professional Spacing */}
      <table className="w-full mb-8">
        <thead>
          <tr className="bg-slate-900 text-white text-[11px] uppercase tracking-wider">
            <th className="p-3 text-left rounded-l">Sl.</th>
            <th className="p-3 text-left">Description of Goods</th>
            <th className="p-3 text-center">HSN/SAC</th>
            <th className="p-3 text-right">Qty</th>
            <th className="p-3 text-right">Rate</th>
            <th className="p-3 text-right">Disc.</th>
            <th className="p-3 text-right rounded-r">Amount</th>
          </tr>
        </thead>
        <tbody className="text-sm border-b border-slate-200">
          {invoice.items.map((item, i) => (
            <tr key={i} className="border-b border-slate-50">
              <td className="p-3 text-slate-400">{i + 1}</td>
              <td className="p-3 font-semibold">{item.name}</td>
              <td className="p-3 text-center text-xs text-slate-500">{item.hsn}</td>
              <td className="p-3 text-right">{item.qty} {item.unit}</td>
              <td className="p-3 text-right">₹{Number(item.rate).toFixed(2)}</td>
              <td className="p-3 text-right text-red-500">-₹{item.discount}</td>
              <td className="p-3 text-right font-bold">
                ₹{(Number(item.qty) * Number(item.rate) - Number(item.discount)).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 4. Bottom Section: Tax Summary & Totals */}
      <div className="flex justify-between gap-10">
        {/* Tax Breakdown Table (Mandatory for Retail Compliance) */}
        <div className="w-3/5">
          <table className="w-full text-[10px] border border-slate-200 text-center">
            <thead className="bg-slate-50 font-bold uppercase">
              <tr>
                <th className="border p-1">Tax Rate</th>
                <th className="border p-1">Taxable Amt</th>
                <th className="border p-1">CGST</th>
                <th className="border p-1">SGST</th>
                <th className="border p-1">Total Tax</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-1">GST 18%</td>
                <td className="border p-1">₹{taxableValue}</td>
                <td className="border p-1">₹{(gstAmount / 2).toFixed(2)}</td>
                <td className="border p-1">₹{(gstAmount / 2).toFixed(2)}</td>
                <td className="border p-1 font-bold">₹{gstAmount}</td>
              </tr>
            </tbody>
          </table>
          
          <div className="mt-6">
            <h4 className="text-[10px] font-bold uppercase text-slate-400 border-b mb-2">Terms & Conditions</h4>
            <ul className="text-[9px] text-slate-500 space-y-1 list-decimal pl-4">
              <li>Warranty provided by the manufacturer only.</li>
              <li>Returns accepted within 48 hours for manufacturing defects.</li>
              <li>This is a Computer Generated Invoice.</li>
            </ul>
          </div>
        </div>

        {/* Totals Box */}
        <div className="w-1/3">
          <div className="space-y-2 border-t-4 border-slate-900 pt-4">
            <div className="flex justify-between text-xs font-medium">
              <span>Total Taxable Value:</span>
              <span>₹{taxableValue}</span>
            </div>
            <div className="flex justify-between text-xs font-medium">
              <span>Total Tax (GST):</span>
              <span>₹{gstAmount}</span>
            </div>
            <div className="flex justify-between py-3 border-t-2 border-double border-slate-900 font-black text-2xl text-slate-900">
              <span>NET PAY:</span>
              <span>₹{invoice.total.toLocaleString()}</span>
            </div>
          </div>
          
          {/* 
          <div className="mt-6 text-center">
            <div className="bg-slate-100 h-10 w-full flex items-center justify-center font-mono text-[8px] tracking-[5px] border-x-2 border-black">
              ||||||| {invoice.invoiceNo} |||||||
            </div>
            <p className="text-[8px] text-slate-400 mt-1 uppercase">Scan for digital copy</p>
          </div> */}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-16 pt-8 border-t border-slate-100 flex justify-between items-end">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-800 underline decoration-slate-300">Privacy Policy</p>
          <p className="text-[9px] text-slate-400 mt-1">Visit {seller.website}/privacy for data usage info.</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-bold uppercase mb-10">Authorized Store Manager</p>
          <div className="border-t border-slate-400 w-40 mx-auto"></div>
        </div>
      </div>
    </div>
  );
};

export default RetailInvoice;